import DatePicker from "react-datepicker";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import React, { FC, Fragment, useState, useEffect, useCallback, useMemo } from "react";
import DatePickerCustomHeaderTwoMonth from "../DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "../DatePickerCustomDay";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import NcInputNumber from "components/NcInputNumber/NcInputNumber";
import { useBooking } from "context/BookingContext";
import { addDays, parseISO, isSameDay, format as formatDate } from "date-fns";
import { useClient } from "ClientContext";
import { useListingBooking } from "context/availabilitiesContext";
import { getDisabledDates } from "utils/getAvailabilitiesConversion";
import { useFetchQuote } from "hooks/useFetchQuote";

//transalation
import { useTranslation } from "react-i18next";
import { set } from "lodash";

interface MobileCalendarProps {
  arrival: string | null;
  departure: string | null;
  setIsQuote?: (isQuote: boolean) => void;
  handleFetchQuote?: ({ listingId }: { listingId: string }) => void;
  renderChildren?: (p: { openModal: () => void }) => React.ReactNode;
}

const today = new Date();

const MobileCalendar: FC<MobileCalendarProps> = ({
  renderChildren,
  arrival,
  departure,
  setIsQuote,
  handleFetchQuote
}): JSX.Element | null => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const { integration_TYPE, global_searh, setGlobal_search, global_max_guests } = useClient();
  const { bookingState, setBookingState } = useBooking();
  const { availabilitiesState } = useListingBooking();
  const calendar = availabilitiesState.availabilities;
  const initialStartDate = new Date(arrival || today);
  const initialEndDate = new Date(departure || addDays(today, 5));
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [guestCount, setGuestCount] = useState<number>(
    global_searh.params.guestsCount || 1
  );

  const { listingId, startingPrice } = bookingState;
  const { quoteError, setError } = useFetchQuote();

  const max_guests = global_max_guests > 1 ? global_max_guests : 25;

  // Normalize all incoming date strings to local-midnight Date objects
  const allLocalMidnights: Date[] = useMemo(() => {
    const dates = calendar || [];
    return dates.map(dateStr => {
      const utcDate = parseISO(dateStr);
      return new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate()
      );
    });
  }, [calendar]);

  // Optimize disabled dates computation
  const disabledDates: Date[] = useMemo(() => {
    if (!startDate) {
      return allLocalMidnights;
    }

    // Find the next disabled date after startDate more efficiently
    const sortedDates = allLocalMidnights
      .filter(date => date > startDate)
      .sort((a, b) => a.getTime() - b.getTime());
    
    const nextDisabledDate = sortedDates[0];

    return allLocalMidnights.filter(date => {
      // Enable check-in date
      if (isSameDay(date, startDate)) {
        return false;
      }
      
      // Enable the next disabled date after check-in
      if (nextDisabledDate && isSameDay(date, nextDisabledDate)) {
        return false;
      }

      return true;
    });
  }, [allLocalMidnights, startDate]);

  const fetchDisabledDates = useCallback(async () => {
    if (!availabilitiesState?.availabilities) return;
    try {
      //setDisabledDates(dates);
    } catch (err) {
      setError(`Error fetching dates: ${(err as Error).message}`);
    }
  }, [availabilitiesState, integration_TYPE, setError]);

  useEffect(() => {
    fetchDisabledDates();
  }, [fetchDisabledDates]);

  // Memoize the date change handler
  const onChangeDate = useCallback((dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setBookingState((prevState) => ({
      ...prevState,
      searchParams: {
        ...prevState.searchParams,
        checkIn: start,
        checkOut: end,
      },
    }));
    setIsQuote && setIsQuote(false);
  }, [setBookingState, setIsQuote]);

  // Memoize the guest count change handler
  const onGuestCountChange = useCallback((value: number) => {
    setGuestCount(value);
    setBookingState((prevState) => ({
      ...prevState,
      searchParams: {
        ...prevState.searchParams,
        guests: value,
      },
    }));
  }, [setBookingState]);

  // Memoize custom header to prevent unnecessary re-renders
  const renderCustomHeader = useCallback(
    (props: any) => <DatePickerCustomHeaderTwoMonth {...props} />,
    []
  );

  // Memoize day contents renderer
  const renderDayContents = useCallback(
    (day: number, date?: Date) => <DatePickerCustomDay dayOfMonth={day} date={date} />,
    []
  );

  const renderButtonOpenModal = () => {
    return renderChildren ? (
      renderChildren({ openModal: () => setShowModal(true) })
    ) : (
      <button onClick={() => setShowModal(true)}>Select Date</button>
    );
  };

  if (!max_guests) {
    return null; 
  }

  return (
    <>
      {renderButtonOpenModal()}
      <Transition appear show={showModal} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setShowModal(false)}
        >
          <div className="fixed inset-0 bg-neutral-100 dark:bg-neutral-900">
            <div className="flex h-full relative">
              <Transition.Child
                as={Fragment}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <Dialog.Panel className="relative h-full overflow-hidden flex-1 flex flex-col justify-between">
                  {/* Close Button */}
                  <div className="absolute top-4 right-4 z-50">
                    <button
                      className="p-2 rounded-full bg-black dark:bg-neutral-800 shadow-md   dark:hover:bg-neutral-700 focus:outline-none"
                      onClick={() => setShowModal(false)}
                    >
                      <XMarkIcon className="w-4 h-4 text-white dark:text-white" />
                    </button>
                  </div>

                  {/* Guest Selector */}
                  <div className="p-5 mt-2 bg-white dark:bg-neutral-800">
                    <span className="block font-semibold text-lg mb-3">
                    {t("Select Dates and Guests")}
                    </span>
                    <div className="w-full">
                      <NcInputNumber
                        className="w-full"
                        defaultValue={guestCount}
                        onChange={onGuestCountChange}
                        max={max_guests}
                        label={t("Guests")}
                        desc={t("Number of people")}
                      />
                    </div>
                  </div>

                  {/* Date Picker */}
                  <div className="flex-1 pt-1 flex flex-col overflow-auto">
                    <div className="flex-1 flex flex-col bg-white dark:bg-neutral-800">
                      <div className="flex-1 flex flex-col overflow-auto p-5">
                        <span className="block font-semibold text-xl sm:text-2xl">
                          {t("When's your trip?")}
                        </span>
                        <div className="overflow-hidden rounded-3xl mt-5">
                          <DatePicker
                            minDate={today}
                            selected={startDate}
                            onChange={onChangeDate}
                            startDate={startDate}
                            endDate={endDate}
                            selectsRange
                            monthsShown={2}
                            showPopperArrow={false}
                            inline
                            excludeDates={disabledDates}
                            renderCustomHeader={renderCustomHeader}
                            renderDayContents={renderDayContents}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer Buttons */}
                  <div className="px-4 py-3 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 flex justify-between">
                    <button
                      type="button"
                      className="underline font-semibold"
                      onClick={() => onChangeDate([null, null])}
                    >
                      {t("Clear dates")}
                    </button>
                    {startDate && endDate && (
                      <ButtonPrimary
                      sizeClass="px-6 py-3 !rounded-xl"
                      onClick={() => {
                        handleFetchQuote && handleFetchQuote({ listingId });
                        setShowModal(false); // Close the modal
                      }}
                      >
                      {t("Apply")}
                      </ButtonPrimary>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default MobileCalendar;